// @fileoverview : 영업관리 > 재고계획

import { mutateStockPlans } from "@/api/stockPlan/useStockPlanQuery";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import { StockPlanCreateForm } from "@/features/stockPlan/form/createForm";
import { StockPlanIncomingForm } from "@/features/stockPlan/form/stockPlanIncomingForm";
import { StockPlanOutgoingForm } from "@/features/stockPlan/form/stockPlanOutgoingForm";
import { StockPlanTransportationForm } from "@/features/stockPlan/form/transportationForm";
import { StockPlanUpdateForm } from "@/features/stockPlan/form/updateForm";
import { TransportationDetailForm } from "@/features/transportation/form/transportationDetailForm";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import timeUtil from "@/utils/timeUtil";
import { ActionIcon, Avatar, Button, Flex, Text, Tooltip } from "@mantine/core";
import {
  StockPlansGet200Response,
  StockPlansGet200ResponseRowsInner,
  WmsApiStockPlansStockPlanIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  IconCodePlus,
  IconEdit,
  IconFilterPlus,
  IconMinus,
  IconPlus,
  IconRecycle,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const StockPlanPage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, setSelectedRows, refetch, resetQueryStrings } = useStandardLayout();
  const { t } = useTranslation();

  const { mutate: deleteMutate } = useMutation(
    (params: WmsApiStockPlansStockPlanIdDeleteRequest) =>
      mutateStockPlans
        .delete(params)
        .mutationFn(params as any | WmsApiStockPlansStockPlanIdDeleteRequest),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          t("message.deleteSuccess", { item: t("stockPlan.stockPlan") }),
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: () => {
        customAlert(
          t("message.deleteFail", { item: t("stockPlan.stockPlan") }),
          t("common.deleteFail"),
          "red",
        );
      },
    },
  );

  const columns: readonly Column<StockPlansGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "scheduledAt",
      name: t("stockPlan.scheduledAt"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return <Flex>{timeUtil(formatterProps.row.scheduledAt ?? "")}</Flex>;
      },
    },
    {
      key: "transportationId",
      name: t("transportation.id"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <TransportationDetailForm transportationID={formatterProps.row.transportationId} />,
                null,
                t("common.detail", { item: t("transportation.transportation") }),
              );
            }}
          >
            {formatterProps.row.transportationId}
          </LinkAnchor>
        );
      },
    },
    {
      key: "partnerCode",
      name: t("partner.partner"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <PartnerDetailForm partnerCode={formatterProps.row.partnerCode} />,
                null,
                t("common.detail", { item: t("partner.partner") }),
              )
            }
          >
            {formatterProps.row.partnerCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "itemCode",
      name: t("item.code"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <ItemsDetailForm itemCode={formatterProps?.row?.itemCode} />,
                null,
                t("common.detail", { item: t("item.item") }),
              )
            }
          >
            {formatterProps.row.itemCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "lotId",
      name: t("lot.lot"),
      sortable: true,
      resizable: true,
    },
    {
      key: "quantity",
      name: t("stockPlan.quantity"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return <Flex justify="flex-end">{formatterProps.row.quantity}</Flex>;
      },
    },
    {
      key: "sourceLocationCode",
      name: t("stockPlan.sourceLocation"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <LocationDetailForm locationCode={formatterProps.row.sourceLocationCode} />,
                null,
                t("common.detail", { item: t("location.location") }),
              )
            }
          >
            {formatterProps.row.sourceLocationCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "targetLocationCode",
      name: t("stockPlan.targetLocation"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <LocationDetailForm locationCode={formatterProps.row.targetLocationCode} />,
                null,
                t("common.detail", { item: t("location.location") }),
              )
            }
          >
            {formatterProps.row.targetLocationCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "purchaseOrderItemId",
      name: t("purchaseOrder.written"),
      sortable: true,
      resizable: true,
    },
    // {
    //   key: "createdAt",
    //   name: "예상 부족 수량",
    //   sortable: true,
    //   resizable: true,
    // },
    {
      key: "options",
      name: t("common.action"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const updateStockPlanActionView = () => {
          openModal(
            <StockPlanUpdateForm formatterProps={formatterProps?.row} />,
            null,
            `${t("common.edit", { item: t("stockPlan.stockPlan") })}`, // 재고계획 수정
            true,
          );
        };

        const deleteStockPlanAction = () => {
          openModal(
            <ConfirmForm
              message={t("message.rowDelete")}
              yesCallback={() => deleteMutate({ stockPlanId: formatterProps?.row.id as number })}
              noCallback={() => {}}
              commonCallback={() => closeModal(null)}
            />,
            null,
            `${t("common.delete", { item: t("stockPlan.stockPlan") })}`, // 재고계획 삭제
          );
        };

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon variant="subtle" onClick={updateStockPlanActionView} color="yellow">
              <IconEdit />
            </ActionIcon>
            <ActionIcon variant="subtle" onClick={() => deleteStockPlanAction()} color="red">
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        `${t("message.deleteSelect", { item: t("stockPlan.stockPlan") })}`,
        `${t("common.deleteFail", { item: t("stockPlan.stockPlan") })}`,
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ stockPlanId: JSON.parse(row).id });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: t("stockPlan.stockPlan") })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <Flex gap="sm">
          <Button
            leftIcon={<IconPlus />}
            onClick={() =>
              openModal(
                <StockPlanCreateForm />,
                null,
                t("common.additional", { item: t("stockPlan.stockPlan") }),
                true,
              )
            }
          >
            {t("common.additional", { item: t("stockPlan.stockPlan") })}
          </Button>
          <Button
            leftIcon={<IconPlus />}
            color="orange"
            // disabled={!isRowBtnActive || data.sourceLocationCode === null}
            disabled={
              !([...selectedRows].length === 1) ||
              ([...selectedRows] as StockPlansGet200ResponseRowsInner).targetLocationCode === null
            }
            onClick={() =>
              openModal(
                <StockPlanIncomingForm formatterProps={JSON.parse([...selectedRows].at(0))} />,
                null,
                t("inventory.incoming"),
              ).then((result) => {
                if (result) {
                  // 로직이 정상적으로 처리되면 선택된 행을 초기화
                  setSelectedRows((): Set<any> => new Set());
                }
              })
            }
          >
            {t("inventory.incoming")}
          </Button>
          <Button
            leftIcon={<IconMinus />}
            disabled={
              !([...selectedRows].length === 1) ||
              ([...selectedRows] as StockPlansGet200ResponseRowsInner).targetLocationCode === null
            }
            color="orange"
            onClick={() =>
              openModal(
                <StockPlanOutgoingForm formatterProps={JSON.parse([...selectedRows].at(0))} />,
                null,
                t("inventory.outgoing"),
              ).then((result) => {
                if (result) {
                  // 로직이 정상적으로 처리되면 선택된 행을 초기화
                  setSelectedRows((): Set<any> => new Set());
                }
              })
            }
          >
            {t("inventory.outgoing")}
          </Button>
          <Button
            leftIcon={<IconCodePlus />}
            color="green"
            disabled={!([...selectedRows].length > 0)}
            onClick={() =>
              openModal(
                <StockPlanTransportationForm
                  stockPlanId={[...selectedRows].map(
                    (row) => (JSON.parse(row) as StockPlansGet200ResponseRowsInner).id as number,
                  )}
                />,
                null,
                t("common.additional", { item: t("transportation.transportation") }),
                true,
              ).then((result) => {
                if (result) {
                  // 로직이 정상적으로 처리되면 선택된 행을 초기화
                  setSelectedRows((): Set<any> => new Set());
                }
              })
            }
          >
            {t("transportation.create")}
          </Button>
        </Flex>
        <Flex gap="sm">
          <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
            {t("common.reset")}
          </Button>
          <Button rightIcon={<IconTrash />} color="red" onClick={() => handleDelete()}>
            {t("common.rowDelete")}
          </Button>
        </Flex>
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          {t("common.list", { item: t("stockPlan.stockPlan") })}
        </Text>
        <Text fz="xs">{t("common.subTitle", { item: t("stockPlan.stockPlan") })}</Text>
        {/* 고객으로부터의 주문 */}
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<StockPlansGet200Response, StockPlansGet200ResponseRowsInner>
            columns={columns}
            rowKeyGetter={(row: StockPlansGet200ResponseRowsInner) => JSON.stringify(row)}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<StockPlansGet200Response> />
      </Flex>
    </Flex>
  );
};
